<template>
  <div class="audioControls" :style="cssProps">
    <div>
      <div
        v-if="!recordAudioState"
        title="Start recording"
        @click="
          permissionStatus === 'denied' || audioIsPlaying === false
            ? recordAudio()
            : false
        "
        class="ar-icon text-white ts-flex ts-items-center ts-justify-center"
        :class="
          permissionStatus === 'denied' || audioIsPlaying
            ? 'recorder-start-error'
            : 'recorder-start pointer'
        "
      >
        <i class="fas fa-microphone fa-2x"></i>
      </div>
      <div
        v-if="recordAudioState"
        title="Stop recording"
        @click="stopRecordAudio"
        class="ar-icon recorder-stop pointer text-white ts-flex ts-items-center ts-justify-center"
      >
        <i class="fas fa-stop fa-2x"></i>
      </div>
    </div>
    <div ref="audio" class="audio audioStyle">
      <audio controls="controls" ref="audioPlayer" class="audioStyle">
        <source :src="audioUrl" type="audio/mpeg" />
      </audio>
    </div>
    <div v-show="recordAudioState && timer">
      <span class="audioTimer" ref="audioTimer"></span>
    </div>
    <div class="upload-student-audio">
      <input
        type="file"
        @change="onFileChange"
        ref="inputFile"
        accept="audio/mpeg, audio/mp4, audio/ogg"
      />
      <a class="fa-stack fa-2x text-primary" @click="$refs.inputFile.click()">
        <i class="fa fa-circle fa-stack-2x"></i>
        <i class="fa fa-upload fa-stack-1x fa-inverse"></i>
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioRecorder',
  props: {
    timer: {
      type: Boolean,
      default: true
    },
    timerColor: {
      type: String,
      default: '#000'
    },
    timerFontSize: {
      type: Number,
      default: 1
    },
    timerBackground: {
      type: String,
      default: '#ccc'
    },
    audioWidth: {
      type: Number,
      default: 200
    },
    resourceId: {
      type: Number,
      default: null
    },
    src: {
      type: String,
      default: ''
    }
  },
  mounted () {
    this.checkPermission()
  },
  data () {
    return {
      permissionStatus: null,
      recorder: null,
      audioIsPlaying: false,
      audioFile: null,
      audioType: 'audio/x-wav',
      audioFileName: 'audio.wav',
      uploadedAudioFile: null,
      recordAudioState: false,
      initialTime: Date.now(),
      saving: false,
      audio_url: ''
    }
  },
  methods: {
    async checkPermission () {
      const status = await navigator.permissions.query({ name: 'microphone' })
      this.permissionStatus = status.state
    },
    clearAudio () {
      const TIMER = this.$refs.audioTimer
      this.initialTime = Date.now()
      TIMER.innerText = ''
    },
    checkAudioTime () {
      const TIMER = this.$refs.audioTimer
      const timeDifference = Date.now() - this.initialTime
      const formatted = this.convertAudioTime(timeDifference)
      TIMER.innerHTML = `${formatted}`
    },
    convertAudioTime (miliseconds) {
      const totalSeconds = Math.floor(miliseconds / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds - minutes * 60
      const sec = seconds < 10 ? `0${seconds}` : seconds
      return `${minutes}:${sec}`
    },
    recordAudio () {
      this.clearAudio()
      try {
        const device = navigator.mediaDevices.getUserMedia({ audio: true })
        const items = []
        device
          .then(stream => {
            this.recorder = new MediaRecorder(stream)
            this.recorder.ondataavailable = e => {
              items.push(e.data)
              if (this.recorder.state === 'inactive') {
                const blob = new Blob(items, { type: this.audioType })
                this.audioFile = URL.createObjectURL(blob)
                this.uploadedAudioFile = blob
                const { audio } = this.$refs
                audio.innerHTML = ''
                const mainaudio = document.createElement('audio')
                mainaudio.setAttribute('controls', 'controls')
                mainaudio.setAttribute('style', `width: ${this.audioWidth}px`)
                audio.appendChild(mainaudio)
                mainaudio.innerHTML = `<source src="${this.audioFile}" type="${this.audioType}" />`
                mainaudio.onplay = () => {
                  this.audioIsPlaying = true
                }
                mainaudio.onpause = () => {
                  this.audioIsPlaying = false
                }
                mainaudio.onended = () => {
                  this.audioIsPlaying = false
                }
              }
            }
            this.recordAudioState = true
            this.audioInterval = setInterval(this.checkAudioTime, 500)
            this.recorder.start()
          })
          .catch(err => {
            // eslint-disable-next-line no-alert
            alert(err)
            // eslint-disable-next-line no-console
            console.log(err)
          })
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert(`Audio error:  ${err}`)
        // eslint-disable-next-line no-console
        console.error('Audio error: ', err)
      }
    },
    stopRecordAudio () {
      clearInterval(this.audioInterval)
      this.recorder.stop()
      this.recordAudioState = false
    },
    deleteAudioFile () {
      if (this.recordAudioState === false) {
        this.audioFile = null
        this.uploadedAudioFile = null
      }
    },
    onFileChange (event) {
      let audioFile = event.target.files[0]

      if (audioFile === undefined) return

      // if (!this.validateExtension(audioFile)) {
      //   this.showInvalideExtensionMessage()
      //   return
      // }

      this.audioFile = audioFile

      this.uploadAudio()
    },
    validateExtension (audioFile) {
      return /(\.mp3)$/i.exec(audioFile.name)
    },
    showInvalideExtensionMessage () {
      this.$toasted.error('Not a valid audio (*.mp3) file.')
    },
    uploadAudio () {
      if (this.audioFile === null) return

      let formData = new FormData()
      formData.append('audio', this.audioFile)
      formData.append('student_id', this.resourceId)

      this.saving = true

      this.$store
        .dispatch('studentProfile/uploadAudio', formData)
        .then(async response => {
          this.saving = false
          this.audio_url = response.data
          await this.$store.dispatch('studentProfile/fetch').catch(error => {
            this.$toasted.error(error.message)
          })

          this.$nextTick(() => {
            this.saving = false
            this.$refs.audioPlayer.load()
          })
        })
    }
  },
  computed: {
    cssProps () {
      return {
        '--font-size': `${this.timerFontSize}em`,
        '--font-color': this.timerColor,
        '--timer-background': this.timerBackground,
        '--audio-width': `${this.audioWidth}px`
      }
    },
    audioUrl () {
      if (this.audio_url) {
        return this.audio_url
      }

      return this.src
    }
  },
  watch: {
    uploadedAudioFile (value) {
      this.$emit('audioFile', value)
    }
  }
}
</script>

<style scoped>
.audioControls {
  display: flex;
  align-items: center;
}

.audioStyle {
  width: var(--audio-width);
}

.ar-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  padding: 5px;
  text-align: center;
}

audio {
  max-height: 100%;
  max-width: 100%;
  margin: auto;
  object-fit: contain;
}

.audio {
  margin: 0 5px;
}
.recorder-start {
  background: green;
}
.recorder-start-error {
  background: gray;
}

.recorder-stop {
  background: red;
  box-shadow: 0 0 0 0 rgba(255, 82, 82, 1);
  animation: pulse 1500ms infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

.audioTimer {
  background: var(--timer-background);
  padding: 5px 20px;
  color: var(--font-color);
  border-radius: 5%;
  font-size: var(--font-size);
  font-weight: bold;
  margin: 0 0 0 5px;
}

.deleteAudio {
  background-color: darkred;
  color: #fff;
  padding: 5px;
  border-radius: 50px;
  width: 30px;
  height: 30px;
  border: none;
  font-weight: bold;
}

.deleteAudio:disabled {
  background-color: palevioletred;
}

.pointer {
  cursor: pointer;
}
</style>
<style lang="scss" scoped>
.upload-student-audio {
  input[type='file'] {
    display: none;
  }

  audio {
    width: 80%;
  }
}
</style>
